import { environment } from '@ag2-weathermaker/shared/environments/environment';
import { ApiService } from '@ag2-weathermaker/shared/services/data/api.service';
import { inject, Injectable } from '@angular/core';
import { AuthConfig, OAuthErrorEvent, OAuthService, TokenResponse } from 'angular-oauth2-oidc';
import { catchError, defer, EMPTY, Observable, take, tap } from 'rxjs';

declare let pendo: any;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiService = inject(ApiService);
  private readonly oauthService = inject(OAuthService);

  private authCodeFlowConfig: AuthConfig = {
    issuer: environment.identityServer,
    tokenEndpoint: environment.identityServer + '/oauth/token',
    redirectUri: window.location.origin + '/auth/callback/signin',
    clientId: environment.clientid,
    responseType: 'code',
    scope: 'openid profile offline_access ag2-weathermaker',
    showDebugInformation: true,
    postLogoutRedirectUri: window.location.origin + '/auth/callback/logout',
  };

  private user = '';

  public initialize(): Promise<void> {
    this.oauthService.configure(this.authCodeFlowConfig);
    this.oauthService.setStorage(localStorage);
    // eslint-disable-next-line no-restricted-syntax -- OAuth library returns Promise, not Observable
    return new Promise((resolve, reject) => {
      this.oauthService
        .loadDiscoveryDocumentAndTryLogin()
        .then(() => {
          if (!this.oauthService.hasValidAccessToken()) {
            // If there's no valid access token, we attempt to initiate the code flow.
            // initCodeFlow does not return a promise, still need to resolve after it has been called
            // to ensure that the APP_INITIALIZER doesn't complete prematurely.
            const state = window.location.pathname + window.location.search;
            this.oauthService.initCodeFlow(state);
          }

          this.oauthService.loadUserProfile().then((data: any) => {
            this.user = data.info.given_name + ' ' + data.info.family_name;
            // if (environment.production) {
            this.initializePendo(data.info.sub);
            // }
            resolve();
          });
        })
        .catch((error) => {
          console.error('OAuthService initialization error:', error);
          reject(error); // Reject the promise on error
        });

      // Handle OAuthService events
      this.oauthService.events.subscribe((event) => {
        if (event instanceof OAuthErrorEvent) {
          console.error('OAuth error event:', event);
        } else {
          console.log('OAuth event:', event);
        }
      });

      // Setup automatic silent refresh
      this.oauthService.setupAutomaticSilentRefresh();
    });
  }

  // Initiate the login process
  public login() {
    this.oauthService.initLoginFlow();
  }

  // Log out and clear all session information
  public logout(): void {
    this.oauthService.logOut();
  }

  public hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  // Check if the user is authenticated
  public isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  // Get the token if needed for manual HTTP requests
  public getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  public getRefreshToken(): string {
    return this.oauthService.getRefreshToken();
  }

  public getIdentityClaims() {
    return this.oauthService.getIdentityClaims();
  }

  public refreshToken(): Observable<TokenResponse> {
    return defer(
      () =>
        // eslint-disable-next-line no-restricted-syntax -- navigator.locks + OAuth APIs are inherently Promise-based
        new Promise<TokenResponse>((resolve, reject) => {
          navigator.locks.request('refreshTokenLock', () =>
            this.oauthService
              .refreshToken()
              .then((response) => {
                console.log('Token refreshed successfully.');
                resolve(response);
              })
              .catch((error) => {
                console.error('Error refreshing token:', error);
                reject(error);
              })
          );
        })
    );
  }

  public getAccessTokenExpiration(): number {
    return this.oauthService.getAccessTokenExpiration();
  }

  public getIdToken(): string {
    return this.oauthService.getIdToken();
  }

  public loadUserProfile(): Promise<object> {
    return this.oauthService.loadUserProfile();
  }

  public getUsername() {
    return this.user;
  }
  public getState() {
    return this.oauthService.state;
  }

  private initializePendo(userId: string) {
    this.apiService
      .getUserSegmentData()
      .pipe(
        take(1),
        tap((segmentData) => {
          pendo.initialize({
            visitor: {
              id: userId,
              email: segmentData.username,
              firstName: segmentData.firstName,
              lastName: segmentData.lastName,
            },
            account: {
              id: segmentData.organizationId,
              accountName: segmentData.organizationName,
            },
          });
        }),
        catchError((error) => {
          console.error('Error fetching user segment data:', error);
          return EMPTY;
        })
      )
      .subscribe();
  }
}
